import ActionButton from "@/components/buttons/ActionButton";
import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import AntIcon from "@/components/icons/AntIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { formatPhoneNumber } from "@/utils/formatUtils";
import { Button, Col, Collapse, Divider, Popconfirm, Row, Space, Tag, Tree } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useAdminDeleteAgentUserAccount,
  useAdminGetUsersByAgent,
  useAdminResendUserConfirmLink,
  useEnumeration,
  useGetPermissionDefinitions,
} from "../../../common/api/queries";
import { useIsSystemAdmin } from "../../auth/hooks";
import { userAccountRemoved } from "../../auth/session";
import { EnumerationType } from "../../enumerations/enums";
import { AdminUpdateUserAccountForm } from "../../user/components/forms/AdminUpdateUserAccountForm";
import { AdminUpdateUserForm } from "../../user/components/forms/AdminUpdateUserForm";
import { AgentUserAccount, UserAdminView } from "../../user/types";
import { buildPermissionTreeNodesView } from "../../user/utils";
import { Agent } from "../types";
import { formatAgentName } from "../utils";
import { AdminCreateUserForAgentForm } from "./forms/AdminCreateUserForAgentForm";

interface Props {
  agent: Agent;
}

export const AgentUserAccounts = ({ agent }: Props) => {
  const { t } = useTranslation();

  const isCurrentUserSystemAdmin = useIsSystemAdmin();
  const topAgentAllowedFeatures = useEnumeration(EnumerationType.TOP_AGENT_ALLOWED_FEATURES);

  const { data: permissionDefinitions } = useGetPermissionDefinitions();

  const [createUserFormOpen, setCreateUserFormOpen] = useState(false);
  const [userToUpdate, setUserToUpdate] = useState<UserAdminView>();
  const [userAccountToUpdate, setUserAccountToUpdate] = useState<{ user: UserAdminView; account: AgentUserAccount }>();

  const { data: users } = useAdminGetUsersByAgent(agent.id);
  const adminDeleteAgentUserAccount = useAdminDeleteAgentUserAccount();
  const adminResendUserConfirmLink = useAdminResendUserConfirmLink();

  return (
    <>
      {users && users.length > 0 ? (
        <Collapse
          accordion
          items={users.flatMap(
            (user) =>
              user.agentUserRole?.userAccounts
                .filter((account) => account.agent?.id === agent.id)
                .map((account) => ({
                  key: account.id,
                  label: account.representingAgent
                    ? `${user.name} | ${formatAgentName(account.representingAgent)}`
                    : user.name,
                  extra: (
                    <Space>
                      {user.locked && <Tag color="red">{t("user.helpers.lockedUser")}</Tag>}
                      {account.disabled && <Tag color="purple">{t("user.helpers.disabledAccount")}</Tag>}
                      {!account.confirmed && <Tag color="orange">{t("user.helpers.unconfirmedUser")}</Tag>}
                      {user.agentUserRole?.systemAdmin && <Tag color="cyan">{t("user.attrs.systemAdmin")}</Tag>}
                    </Space>
                  ),
                  children: (
                    <>
                      <Divider titlePlacement="start" style={{ marginTop: 0 }}>
                        {t("user.titles.basicData")}
                      </Divider>

                      <ItemCreatedUpdatedInfoView item={user} className="margin-bottom-medium" />

                      <table className="data-table-view margin-top-medium margin-bottom-small">
                        <tbody>
                          <tr>
                            <td>{t("user.attrs.name")}:</td>
                            <td>{user.name}</td>
                            <td>
                              {t("user.attrs.wrongLoginAttempts")}: {user.wrongLoginAttempts}
                            </td>
                          </tr>
                          <tr>
                            <td>{t("user.attrs.email")}:</td>
                            <td>{user.email || user.emailToConfirm}</td>
                            <td>
                              {!account.confirmed && (
                                <Popconfirm
                                  title={t("user.titles.resendConfirmLinkConfirm")}
                                  icon={<PopconfirmDeleteIcon />}
                                  okText={t("common.yes")}
                                  cancelText={t("common.no")}
                                  okType="danger"
                                  onConfirm={() => adminResendUserConfirmLink.mutate({ id: user.id })}
                                >
                                  <SecondaryButton size="small" type="primary" icon={<AntIcon type="mail" />}>
                                    {t("user.actions.resendConfirmLink")}
                                  </SecondaryButton>
                                </Popconfirm>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td>{t("user.attrs.phone")}:</td>
                            <td>{formatPhoneNumber((user.phone || user.phoneToConfirm) ?? "")}</td>
                          </tr>
                        </tbody>
                      </table>

                      <Button
                        className="margin-bottom-tiny"
                        size="small"
                        type="primary"
                        icon={<AntIcon type="edit" />}
                        onClick={() => setUserToUpdate(user)}
                      >
                        {t("user.actions.updateBasicUserData")}
                      </Button>

                      <Divider titlePlacement="start">{t("user.titles.account")}</Divider>

                      <ItemCreatedUpdatedInfoView item={account} className="margin-bottom-medium" />

                      {account.representingAgent && (
                        <p className="margin-bottom-medium">
                          <b>{t("user.attrs.accounts.representingAgentId")}:</b>{" "}
                          {formatAgentName(account.representingAgent)}
                        </p>
                      )}

                      <h4>{t("user.attrs.permissionsLabel")}</h4>
                      <Tree
                        showLine={{ showLeafIcon: false }}
                        treeData={buildPermissionTreeNodesView(
                          permissionDefinitions,
                          topAgentAllowedFeatures,
                          account.permissions,
                          isCurrentUserSystemAdmin,
                        )}
                      />

                      <Space className="margin-top-medium">
                        <Button
                          type="primary"
                          size="small"
                          icon={<AntIcon type="edit" />}
                          onClick={() => setUserAccountToUpdate({ user, account })}
                        >
                          {t("user.actions.updateAccount")}
                        </Button>

                        <Popconfirm
                          title={
                            <div style={{ maxWidth: 450 }}>
                              <Row>
                                <Col>
                                  {t("user.titles.deleteAgentAccountConfirm", {
                                    aggregatedName: account.agent?.aggregatedName,
                                  })}
                                </Col>
                              </Row>
                              <Row>
                                <Col>{t("user.helpers.updateInfo")}</Col>
                              </Row>
                            </div>
                          }
                          icon={<PopconfirmDeleteIcon />}
                          okText={t("common.yes")}
                          cancelText={t("common.no")}
                          okType="danger"
                          onConfirm={() =>
                            adminDeleteAgentUserAccount.mutate(
                              { id1: user.id, id2: account.id },
                              {
                                onSuccess: () => {
                                  userAccountRemoved(user.id, account.id);
                                },
                              },
                            )
                          }
                        >
                          <ActionButton icon="minus">{t("user.actions.deleteAccount")}</ActionButton>
                        </Popconfirm>
                      </Space>
                    </>
                  ),
                })) || [],
          )}
        />
      ) : (
        <div className="center-align sub-header-info dashed">{t("user.helpers.noUserForAgent")}</div>
      )}

      <ActionButton icon="plus" className="margin-top-small" onClick={() => setCreateUserFormOpen(true)}>
        {t("user.actions.createUser")}
      </ActionButton>

      <AdminCreateUserForAgentForm
        open={createUserFormOpen}
        agent={agent}
        onFormCancel={() => setCreateUserFormOpen(false)}
      />

      <AdminUpdateUserForm open={!!userToUpdate} user={userToUpdate} onFormCancel={() => setUserToUpdate(undefined)} />

      <AdminUpdateUserAccountForm
        open={!!userAccountToUpdate}
        user={userAccountToUpdate?.user}
        account={userAccountToUpdate?.account}
        onFormCancel={() => setUserAccountToUpdate(undefined)}
      />
    </>
  );
};
